import {
    useEffect,
    useId,
    useRef,
    useState
} from "react";
import { 
    Component_Display, 
    Component_Display_Address, 
    Component_Display_Contact, 
    Component_Fieldset, 
    Component_Form_Address, 
    Component_Form_Contact, 
    Component_Loading, 
    Component_Modal, 
    Component_Row,
    Component_Step,
    type DropdownOption
} from "@eifu-development/endurien_react_components";
import { 
    Api_Consumer_Eifu, 
    type Eifu_Translation_Map, 
    type Eifu_User_Info, 
    type Settings_Api_Consumer_Eifu 
} from "@eifu-development/eifu-api-consumer";
import {
    default as Component_Order,
    type Eifu_Order_Line_Item_Display as Order_Line_Item
} from "@eifu-development/eifu-react-components/components/order";

import "@eifu-development/endurien_react_components/dist/endurien_react_components.css";
import styles from "./assets/css/Console_Order_Modal.module.css";

const Translations = {
    AddressLineOne: "strAddressLineOne",
    AddressLineTwo: "strAddressLineTwo",
    Cancel: "BtnCancel",
    City: "strCity",
    ContactInformation: "lblContact",
    Country: "strCountry",
    Email: "strEmail",
    Extension: "strContactInfoExtension",
    FaxNumber: "lblFaxNumber",
    FirstName: "strFirstName",
    LastName: "strLastName",
    PhoneNumber: "strPhone",
    ShippingAddress: "lblShippingAddress",
    State: "strState",
    PostalCode: "strZipCode",
    UpdateAndSave: "BtnSaveUpdate",
    Next: "BtnNext",
    Back: "BtnBack",
    Order: "strOrder",
    Confirm: "btnConfirm",
    StepOneHeader: "strOrderStep1",
    StepTwoHeader: "strOrderStep2",
    StepThreeHeader: "strOrderStep3",
    StepFourHeader: "strOrderStep4",
    OrderPlaced: "strOrderPlaced",
    OrderFailed: "strOrderFailed",
    Orders: "lblOrderSummary",
    Quantity: "strQuantity",
    Name: "strName",
    Description: "strDescription",
    FileType: "strFileType",
    Done: "btnDone",
    TransactionNumber: "strTransactionNumber"
} as const;

const TranslationKeys = Object.values(Translations);

const EifuCss = {
    Button: "btn btn-default",
    Select: "radInput",
    InputTextLabel: "riSingle RadInput RadInput_Default",
    InputText: "riTextBox radInput",
    Modal: "ui-widget ui-widget-content",
    ModalHeader: "ui-dialog-titlebar ui-corner-all ui-widget-header"
};

export interface Props_Console_Order_Modal {
    id: string;
    settings: Settings_Api_Consumer_Eifu;
    lineItems: Order_Line_Item[];
}

const Console_Order_Modal: React.FC<Props_Console_Order_Modal> = ({
    id,
    settings,
    lineItems
}) => {

    // Make sure ID has a value
    const idUnique = useId();
    id = id || idUnique;
    const idModal = `${id}_modal`;
    const idStepOne = `${id}_step_one`;
    const idStepTwo = `${id}_step_two`;
    const idStepThree = `${id}_step_three`;
    const idStepFour = `${id}_step_four`;
    const idFormAddress = `${id}_form_address`;
    const idFormContact = `${id}_form_contact`;
    const idDisplayAddress = `${id}_display_address`;
    const idDisplayContact = `${id}_display_contact`;
    const idDisplayOrders = `${id}_display_orders`;
    const idOrders = `${id}_orders`;

    const [index, setIndex] = useState<number>(0);
    const [hidden, setHidden] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [translations, setTranslation] = useState<Eifu_Translation_Map>({});
    const [countries, setCountries] = useState<DropdownOption[]>([]);
    const [userInfo, setUserInfo] = useState<Eifu_User_Info>({});
    const [hiddenStepOne, setHiddenStepOne] = useState<boolean>(false);
    const [hiddenStepTwo, setHiddenStepTwo] = useState<boolean>(true);
    const [hiddenStepThree, setHiddenStepThree] = useState<boolean>(true);
    const [hiddenStepFour, setHiddenStepFour] = useState<boolean>(true);
    const [transactionNumber, setTransactionNumber] = useState<string>();
    const [quantities, setQuantities] = useState<number[]>(() =>
        lineItems.map(lineItem => lineItem.payload.Quantity ?? lineItem.quantity ?? 1)
    );

    const apiConsumer = useRef(Api_Consumer_Eifu.getInstance(settings)).current;

    useEffect(() => {
        if (index < 0 || index > 3)
            return;
        setHiddenStepOne(index !== 0);
        setHiddenStepTwo(index !== 1);
        setHiddenStepThree(index !== 2);
        setHiddenStepFour(index !== 3);
    }, [index]);

    useEffect(() => {
        setLoading(true);
        const load = async () => {
            const resultsTranslations = await apiConsumer.translationMapFetch(TranslationKeys);
            setTranslation(resultsTranslations);

            const resultsCountries = await apiConsumer.countriesFetch();
            const countries = resultsCountries.reduce((acc, country) => {
                acc.push({ [country.CountryName]: country.CountryCode });
                return acc;
            }, [] as DropdownOption[])
            setCountries(countries);

            const resultsUserInfo = await apiConsumer.userInfoFetch();
            setUserInfo(resultsUserInfo);

            setLoading(false);
        }
        load();
    }, [apiConsumer]);

    const next = () => {
        setIndex(previousIndex => previousIndex + 1);
    };

    const back = () => {
        setIndex(previousIndex => previousIndex - 1);
    };

    const order = () => {
        setLoading(true);
        const orderCreate = async () => {
            const result = await apiConsumer.orderCreate({
                Order: {
                    LineItems: lineItems.map((lineItem, itemIndex) => ({
                        ...lineItem.payload,
                        Quantity: quantities[itemIndex] || 1
                    }))
                },
                UserInfo: userInfo
            });
            if (result) {
                setTransactionNumber(result);
                next();
            } else {
                alert(translations[Translations.OrderFailed]);
            }

            setLoading(false);
        };
        orderCreate();
    };
    
    const onInputChangeAddress = (key: string, value: string | undefined) => {
        setUserInfo(previousState => ({
            ...previousState,
            Address: { 
                ...previousState.Address,
                [key]: value 
            }
        }));
    };

    const onInputChangeContact = (key: string, value: string | undefined) => {
        setUserInfo(previousState => ({
            ...previousState,
            Contact: { 
                ...previousState.Contact,
                [key]: value 
            }
        }));
    };

    const hide = () => setHidden(true);

    return (
        <>
            <Component_Modal
                id={idModal}
                settings={{
                    className: `${styles.order_modal} ${loading || !hiddenStepFour ? styles.order_modal_status : ""} ${EifuCss.Modal}`,
                    hidden: hidden,
                    header: {
                        text: translations[Translations.Order] || "Order",
                        className: `${styles.modal_header} ${EifuCss.ModalHeader}`,
                        close: {
                            onClick: hide
                        }
                    },
                    body: {
                        className: styles.modal_body
                    }
                }}
            >
                {
                    loading
                    ?
                    <Component_Loading
                        settings={{
                            className: styles.loading
                        }}
                    />
                    :
                    <>
                        <Component_Step
                            id={idStepOne}
                            settings={{
                                hidden: hiddenStepOne,
                                className: styles.step,
                                header: translations[Translations.StepOneHeader] || "Step 1: Recipient's Address",
                                row: {
                                    className: styles.step_actions
                                },
                                action: { 
                                    text: translations[Translations.Next] || "Next",
                                    className: EifuCss.Button,
                                    form: idFormAddress,
                                    type: "submit"
                                },
                                back: { hidden: true }
                            }}
                        >
                            <Component_Form_Address
                                id={idFormAddress}
                                settings={{
                                    className: styles.form,
                                    onSubmit: (event) => {
                                        event.preventDefault();
                                        next();
                                    },
                                    row: {
                                        className: styles.form_row
                                    },
                                    fieldset: {
                                        legend: translations[Translations.ShippingAddress]
                                    },
                                    firstName: {
                                        value: userInfo?.Address?.FirstName,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeAddress("FirstName", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.FirstName],
                                            className: EifuCss.InputTextLabel,

                                        }
                                    },
                                    lastName: {
                                        value: userInfo?.Address?.LastName,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeAddress("LastName", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.LastName],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    },
                                    addressLineOne: {
                                        value: userInfo?.Address?.AddressLineOne,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeAddress("AddressLineOne", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.AddressLineOne],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    },
                                    addressLineTwo: {
                                        value: userInfo?.Address?.AddressLineTwo,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeAddress("AddressLineTwo", newValue),
                                        label: {
                                            text: translations[Translations.AddressLineTwo],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    },
                                    city: {
                                        value: userInfo?.Address?.City,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeAddress("City", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.City],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    },
                                    state: {
                                        value: userInfo?.Address?.State,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeAddress("State", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.State],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    },
                                    country: {
                                        options: countries,
                                        className: EifuCss.Select,
                                        value: userInfo?.Address?.Country,
                                        setValue: newValue => onInputChangeAddress("Country", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.Country],
                                        }
                                    },
                                    postalCode: {
                                        value: userInfo?.Address?.PostalCode,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeAddress("PostalCode", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.PostalCode],
                                            className: EifuCss.InputTextLabel,
                                        }
                                }}}
                            />
                        </Component_Step>
                        <Component_Step
                            id={idStepTwo}
                            settings={{
                                hidden: hiddenStepTwo,
                                className: styles.step,
                                header: translations[Translations.StepTwoHeader] || "Step 2: Contact",
                                row: {
                                    className: styles.step_actions
                                },
                                action: { 
                                    text: translations[Translations.Next] || "Next",
                                    className: EifuCss.Button,
                                    form: idFormContact,
                                    type: "submit"
                                },
                                back: {
                                    text: translations[Translations.Back] || "Back",
                                    className: EifuCss.Button,
                                    onClick: back
                                }
                            }}
                        >
                            <Component_Form_Contact
                                id={idFormContact}
                                settings={{
                                    className: styles.form,
                                    onSubmit: (event) => {
                                        event.preventDefault();
                                        next();
                                    },
                                    row: {
                                        className: styles.form_row
                                    },
                                    fieldset: {
                                        legend: translations[Translations.ContactInformation]
                                    },
                                    phoneNumber: {
                                        value: userInfo?.Contact?.PhoneNumber,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeContact("PhoneNumber", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.PhoneNumber],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    },
                                    extension: {
                                        value: userInfo?.Contact?.Extension,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeContact("Extension", newValue),
                                        label: {
                                            text: translations[Translations.Extension],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    },
                                    faxNumber: {
                                        value: userInfo?.Contact?.FaxNumber,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeContact("FaxNumber", newValue),
                                        label: {
                                            text: translations[Translations.FaxNumber],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    },
                                    email: {
                                        value: userInfo?.Contact?.Email,
                                        className: EifuCss.InputText,
                                        setValue: newValue => onInputChangeContact("Email", newValue),
                                        required: true,
                                        label: {
                                            text: translations[Translations.Email],
                                            className: EifuCss.InputTextLabel,
                                        }
                                    }
                                }}
                            />
                        </Component_Step>
                        <Component_Step
                            id={idStepThree}
                            settings={{
                                hidden: hiddenStepThree,
                                className: styles.step,
                                header: translations[Translations.StepThreeHeader] || "Step 3: Review & Confirm",
                                row: {
                                    className: styles.step_actions
                                },
                                action: {
                                    text: translations[Translations.Confirm] || "Confirm",
                                    className: EifuCss.Button,
                                    onClick: order
                                },
                                back: {
                                    text: translations[Translations.Back] || "Back",
                                    className: EifuCss.Button,
                                    onClick: back
                                }
                            }}
                        >
                            <Component_Row
                                settings={{
                                    className: styles.review_row
                                }}
                            >
                                <Component_Fieldset
                                    settings={{
                                        className: styles.order_fieldset_display_address
                                    }}
                                >
                                    <Component_Display_Address
                                        id={idDisplayAddress}
                                        settings={{
                                            header: translations[Translations.ShippingAddress],
                                            firstName: userInfo?.Address?.FirstName,
                                            lastName: userInfo?.Address?.LastName,
                                            addressLineOne: userInfo?.Address?.AddressLineOne,
                                            addressLineTwo: userInfo?.Address?.AddressLineTwo,
                                            city: userInfo?.Address?.City,
                                            state: userInfo?.Address?.State,
                                            country: userInfo?.Address?.Country,
                                            postalCode: userInfo?.Address?.PostalCode   
                                        }}
                                    />
                                    <Component_Display_Contact
                                        id={idDisplayContact}
                                        settings={{
                                            header: translations[Translations.ContactInformation],
                                            phoneNumber: userInfo?.Contact?.PhoneNumber,
                                            extension: userInfo?.Contact?.Extension,
                                            faxNumber: userInfo?.Contact?.FaxNumber,
                                            email: userInfo?.Contact?.Email
                                        }}
                                    />
                                </Component_Fieldset>
                                <Component_Fieldset
                                    settings={{
                                        className: styles.order_fieldset_display_contact
                                    }}
                                >
                                    <Component_Display
                                        id={idDisplayOrders}
                                        settings={{
                                            header: translations[Translations.Orders] || "Orders",
                                        }}
                                    >
                                        <Component_Order
                                            id={idOrders}
                                            settings={{
                                                lineItems: lineItems.map((lineItem, itemIndex) => ({
                                                    ...lineItem,
                                                    quantity: quantities[itemIndex] || 1
                                                })),
                                                quantityLabel: translations[Translations.Quantity] || "Quantity",
                                                nameLabel: translations[Translations.Name] || "Name",
                                                descriptionLabel: translations[Translations.Description] || "Description",
                                                fileTypeLabel: translations[Translations.FileType] || "File Type",
                                                setQuantity: (itemIndex, quantity) => setQuantities(previousQuantities =>
                                                    previousQuantities.map((previousQuantity, previousIndex) =>
                                                        previousIndex === itemIndex ? quantity : previousQuantity
                                                    )
                                                )
                                            }}
                                        />
                                    </Component_Display>
                                </Component_Fieldset>
                            </Component_Row>
                        </Component_Step>
                        <Component_Step
                            id={idStepFour}
                            settings={{
                                hidden: hiddenStepFour,
                                className: styles.step,
                                header: translations[Translations.StepFourHeader] || "Order Placed",
                                row: {
                                    className: styles.step_actions
                                },
                                action: { 
                                    text: translations[Translations.Done] || "Done",
                                    className: EifuCss.Button,
                                    onClick: hide
                                },
                                back: {
                                    hidden: true
                                }
                            }}
                        >
                            <div className={styles.confirmation_row}>
                                <span>{translations[Translations.OrderPlaced]}</span>
                                <h3>{translations[Translations.TransactionNumber]}</h3>
                                <span>{transactionNumber}</span>
                            </div>
                        </Component_Step>
                    </>
                }
            </Component_Modal>
        </>
    );
};

export default Console_Order_Modal;
